import {
  Component,
  ChangeDetectionStrategy,
  OnInit,
  HostBinding,
  ChangeDetectorRef,
  ViewChild,
  ElementRef,
  input,
  inject,
} from '@angular/core';
import { IsActiveMatchOptions, NavigationEnd, Router } from '@angular/router';

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { combineLatest } from 'rxjs';
import { filter } from 'rxjs/operators';

import { SAJMenuComponent, SAJMenuTriggerDirective } from '@snagajob/menu';

import {
  AuthenticationService,
  IUserAccount,
  MarketplacePermission,
} from '@libs/authentication/services';
import { ReportingPageSection } from '@libs/types';
import { AdobeService } from '@libs/services';
import { BaseMenuItem, CategoryId, NavMenuItem } from '../types/menu';
import { NavigationMenu } from '../types/menu/nav-menu-interface';
import { MenuService } from '../services/menu-service';
import { MenuIds } from '../types/menu/nav-menu';
import { AccountPickerService } from '../services/account-picker.service';
import { Account, AccountsApiService } from '@libs/customers/accounts';
import { SAJModalService } from '@snagajob/modal';
import { SidebarService } from '@hub/page';

@UntilDestroy()
@Component({
  templateUrl: 'sidebar-navigation.component.html',
  selector: 'hub-sidebar-navigation',
  styleUrls: ['./sidebar-navigation.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SidebarNavigationComponent implements OnInit {
  private readonly sidebarService = inject(SidebarService);

  isMobile = input<boolean>(false);
  isTabletLarge = input<boolean>(false);

  @HostBinding() classList = 'flex flex-col h-full pt-24 pb-12';
  @ViewChild('accountInfoMenuTrigger')
  accountInfoMenuTrigger: SAJMenuTriggerDirective;

  @ViewChild('accountInfoMenu') accountInfoMenu: SAJMenuComponent;
  @ViewChild('settingsMenu') settingsMenu: ElementRef;
  @ViewChild('switchAccountMenu') switchAccountMenu: ElementRef;

  // Menus and Sub Menus
  navMenu: NavigationMenu = {} as NavigationMenu;
  MenuIds = MenuIds;
  accountInfoMenuOpened = false;
  settingsMenuOpened = false;
  switchAccountMenuOpened = false;
  accordionPanels = [];
  linkActiveOptions = {
    paths: 'exact',
    queryParams: 'ignored',
    fragment: 'ignored',
    matrixParams: 'ignored',
  } as IsActiveMatchOptions;

  // Account Info
  userAccounts: IUserAccount[];
  showSwitchAccounts = false;
  account: Account;
  user: MarketplacePermission;

  toggledAccordions = {};

  // collapsed exists only in tablet or higher
  isCollapsed = this.sidebarService.isCollapsed;

  private readonly modalService = inject(SAJModalService);
  private readonly auth = inject(AuthenticationService);
  private readonly adobeService = inject(AdobeService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly menuService = inject(MenuService);
  private readonly accountsApiService = inject(AccountsApiService);
  private readonly accountPickerService = inject(AccountPickerService);
  private readonly router = inject(Router);

  ngOnInit(): void {
    this.getAccountInfo();

    const canUseAccountPicker = this.accountPickerService.canUseAccountPicker();
    if (canUseAccountPicker) {
      this.loadUserAccounts();
    } else {
      this.showSwitchAccounts = false;
      this.cdr.markForCheck();
    }

    // Ensures that isActiveRoute updates the state correctly
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        untilDestroyed(this),
      )
      .subscribe(() => {
        if (this.isCollapsed()) {
          this.cdr.markForCheck();
        }
      });
  }

  isActiveRoute(panel): boolean {
    return panel.items.some((navMenuItem) => {
      return this.router.isActive(navMenuItem.route, this.linkActiveOptions);
    });
  }

  getAccountInfo(): void {
    combineLatest([
      this.auth.permission$,
      this.accountsApiService.currentAccount$,
    ])
      .pipe(
        untilDestroyed(this),
        filter(([permission]) => permission?.claims?.length > 0),
      )
      .subscribe(([permission, account]) => {
        if (permission) {
          const user: MarketplacePermission = this.auth.permission$.value;
          this.setNavMenuItems();
          this.setSettingsMenuItems();
          this.user = user;
        }

        if (account) {
          this.account = account;
        }
        this.cdr.markForCheck();
      });
  }

  trackClick(navLabel: string): void {
    this.adobeService.trackClick(ReportingPageSection.NavMenu, navLabel);
  }

  setNavMenuItems(): void {
    const initialMenuItems = this.menuService.getNavMenu();

    const navMenuItems = initialMenuItems.filter((menuItem: NavMenuItem) =>
      this.shouldFilterMenuItem(menuItem),
    );

    this.navMenu.mainItems = navMenuItems.filter(
      (item) => item.categoryId === CategoryId.Main,
    );
    this.navMenu.reportItems = navMenuItems.filter(
      (item) => item.categoryId === CategoryId.Reports,
    );
    this.navMenu.organizationItems = navMenuItems.filter(
      (item) => item.categoryId === CategoryId.Organization,
    );

    this.accordionPanels = []; // to avoid reportItems and organizationItems duplication when user switches accounts
    if (this.navMenu.reportItems.length) {
      this.accordionPanels.push({
        header: 'Reports',
        items: [...this.navMenu.reportItems],
        isOpened: false,
      });
    }

    if (this.navMenu.organizationItems.length) {
      this.accordionPanels.push({
        header: 'Organization',
        items: [...this.navMenu.organizationItems],
        isOpened: false,
      });
    }
    this.cdr.detectChanges();
  }

  setSettingsMenuItems(): void {
    const initialMenuItems = this.menuService.getAccountSettingsNavMenu();
    this.navMenu.settingsItems = initialMenuItems
      .filter((menuItem: BaseMenuItem) => this.shouldFilterMenuItem(menuItem))
      .map((menuItem: NavMenuItem) => {
        if (this.auth.hasSelfSignup() && menuItem.selfSignupLabel) {
          return { ...menuItem, label: menuItem.selfSignupLabel };
        }
        return { ...menuItem };
      });
  }

  shouldFilterMenuItem(menuItem: BaseMenuItem): boolean {
    const hasDenyClaim =
      menuItem.denyClaims && this.auth.hasClaims(menuItem.denyClaims);
    if (!this.auth.isImpersonatedUser() && hasDenyClaim) {
      return false;
    }

    // hide campaigns pages if LegacyECommAccount
    if (
      this.auth.isLegacyECommAccount() &&
      (menuItem.id === MenuIds.Promote ||
        menuItem.id === MenuIds.CampaignPerformance)
    ) {
      return false;
    }

    const hasClaims = menuItem.claims && this.auth.hasClaims(menuItem.claims);
    const isSelfSignupOverride = menuItem.enabledUsingSelfSignup
      ? !this.auth.hasSelfSignup()
      : true;
    return (hasClaims && isSelfSignupOverride) || menuItem.enabled;
  }

  navigationHandler(label: string = null): void {
    if (label) {
      this.trackClick(label);
    }

    if (this.accountInfoMenuOpened) {
      this.accountInfoMenuTrigger.close();
    }
    if (this.isMobile()) {
      this.toggleSideNav();
    }
  }

  switchAccount(permissionId: string): void {
    this.accountPickerService.switchAccount(permissionId);

    if (this.accountInfoMenuOpened) {
      this.accountInfoMenuTrigger.close();
    }
  }

  toggleSideNav() {
    if (this.isMobile()) {
      this.sidebarService.isMobileOpened.update((isOpened) => !isOpened);
    } else {
      this.sidebarService.collapsedState.update((isCollapsed) => !isCollapsed);
    }
  }

  private loadUserAccounts(): void {
    this.accountPickerService
      .getUserAccounts()
      .subscribe((userAccounts: IUserAccount[]) => {
        this.userAccounts = userAccounts;
        this.showSwitchAccounts = userAccounts.length > 0;
        this.cdr.markForCheck();
      });
  }

  // this is used to render accordion component properly for mobile sidenav, when it's hidden at the beginning
  accordionToggled(name: string) {
    this.toggledAccordions[name] = true;
  }

  async openContactSupportModal() {
    const { ContactSupportModalComponent } = await import('@libs/help-center');

    this.modalService.open(ContactSupportModalComponent, {
      ariaLabel: 'Contact support',
      data: {},
    });
  }

  async openShareFeedbackModal() {
    const { ShareFeedbackModalComponent } = await import('@libs/help-center');

    this.modalService.open(ShareFeedbackModalComponent, {
      ariaLabel: 'Share feedback',
      data: {},
    });
  }

  openGuide() {
    window.open(
      'https://snagajob.gitbook.io/snagajob-employer-guide',
      '_blank',
    );
  }
}
